import { useFormContext, useFormState } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Button, CheckboxStateValues, Icon, Text, TextInput } from "@ucl/react-library";
import { useBoundInput } from "src/hooks/use-bound-input";
import AddressSection from "./address-section/address-section";
import { ImportantInformation } from "../important-information/important-information";
import { InlineAlertMessage } from "../inline-alert-message/inline-alert-message";
import { translationMapping } from "src/utils/translation-mappings";
import { ContactPreferenceUpdate } from "../contact-info/contact-preference-update";
import { useEffect, useState } from "react";


type ContactDetailsEditProps = {
  showContactPreference?: boolean;
  showSubmitButton?: boolean;
  toggleContactDetails?: () => void;
  showError?: boolean;
  onClose?: () => void;
};

export const ContactDetailsEdit = ({ showContactPreference = false, showSubmitButton = false, toggleContactDetails, showError, onClose }: ContactDetailsEditProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "editContactDetails" });
  const { t: genericT } = useTranslation();
  const [displayError, setDisplayError] = useState(showError);

  const { control, watch } = useFormContext();
  const { errors } = useFormState({ control });

  const fullNameProps = useBoundInput({ name: "fullName", control });
  const emailProps = useBoundInput({ name: "email", control });
  const phoneProps = useBoundInput({ name: "homePhone", control });

  const checked = watch("agreeTsAndCs");

  const isAgreed = checked === CheckboxStateValues.CHECKED;
  useEffect(() => {
    if (errors.address || errors.preferredContactChannel) {
      setDisplayError(true);
    }
  }, [errors.address, errors.preferredContactChannel]);

  return (
    <div data-testid={"editContactDetails"} className="flex flex-col gap-10">
      <div className="flex flex-col items-start gap-5">
        <div className="flex flex-col gap-2 w-full">
          <TextInput
            data-testid="fullName"
            labelProp={{
              label: t("name"),
            }}
            disabled={true}
            placeholder=""
            {...fullNameProps}
          />
          <Text.P size="large"><Trans i18nKey={"editContactDetails.updateName"} components={translationMapping(genericT)} /></Text.P>
        </div>
        <TextInput
          data-testid="emailAddress"
          labelProp={{
            label: t("email"),
          }}
          placeholder=""
          type="email"
          {...emailProps}
        />
        <TextInput
          data-testid="phoneNumber"
          labelProp={{
            label: t("phoneNumber"),
          }}
          placeholder=""
          type="tel"
          minLength={10}
          {...phoneProps}
        />
        <Text renderAs="h3" styleType="h4" fontWeight="semi-bold" className="pt-5">{t("address")}</Text>
        <AddressSection />
        {displayError && (<InlineAlertMessage title={genericT("editContactDetails.errors.checkDetails")} body={""} onClose={onClose} />)}
      </div>
      {(showContactPreference || showSubmitButton) &&
        <div className="flex flex-col gap-5" data-testid="updateContactDetailsSection">
          <Text renderAs="h3" styleType="h4" fontWeight="semi-bold">
            {genericT("importantInformation.title")}
          </Text>
          {showContactPreference && <ContactPreferenceUpdate />}
          {showSubmitButton &&
            <div className="flex flex-col gap-5">
              <ImportantInformation showOptInInfo={false} />
              {showError && <InlineAlertMessage
                title={genericT("errors.updateGeneric.title")}
                body={<Trans i18nKey={"errors.updateGeneric.body"} components={translationMapping(genericT)} />}
                onClose={onClose}
              />}
              <div className="flex flex-col gap-4 sm:flex-row-reverse sm:!justify-between">
                <Button type="submit" disabled={!isAgreed}>{t("updateDetails")}</Button>
                <Button variant="tertiary" onClick={toggleContactDetails}>
                  <Icon icon="caretLeft" />
                  {t("cancel")}
                </Button>
              </div>
            </div>
          }
        </div>
      }
    </div>
  );
};
